"use client"

import { useEffect, useState } from "react"
import { useRouter } from "next/navigation"

export interface AuthUser {
  id: string
  username: string
  role: 'USER' | 'ADMIN'
  isDeveloper: boolean
  isBlocked: boolean
  avatarUrl?: string | null
}

interface AuthSession {
  user: AuthUser
}

interface UseAuthReturn {
  data: AuthSession | null
  status: 'loading' | 'authenticated' | 'unauthenticated'
  update: () => Promise<void>
}

/**
 * Hook do sprawdzania sesji z Lucia Auth
 * Zastępuje useSession z NextAuth
 * Używa localStorage do cachowania i zmniejszenia liczby requestów
 */
export function useAuth(): UseAuthReturn {
  const [session, setSession] = useState<AuthSession | null>(null)
  const [status, setStatus] = useState<'loading' | 'authenticated' | 'unauthenticated'>('loading')

  const fetchSession = async (useCache = true) => {
    try {
      // Sprawdź cache (ważny przez 5 minut)
      if (useCache && typeof window !== 'undefined') {
        const cached = localStorage.getItem('auth_cache')
        if (cached) {
          try {
            const { data, timestamp } = JSON.parse(cached)
            const CACHE_DURATION = 5 * 60 * 1000 // 5 minut

            if (Date.now() - timestamp < CACHE_DURATION) {
              setSession(data.authenticated ? { user: data.user } : null)
              setStatus(data.authenticated ? 'authenticated' : 'unauthenticated')
              // Fetch w tle aby odświeżyć cache
              fetchSessionFromServer()
              return
            }
          } catch (e) {
            // Błąd parsowania - usuń uszkodzony cache
            localStorage.removeItem('auth_cache')
          }
        }
      }

      // Brak cache lub wygasł - pobierz z serwera
      await fetchSessionFromServer()
    } catch (error) {
      console.error('[useAuth] Error fetching session:', error)
      setSession(null)
      setStatus('unauthenticated')
    }
  }

  const fetchSessionFromServer = async () => {
    try {
      const res = await fetch('/api/auth/session')
      const data = await res.json()

      // Zapisz w cache
      if (typeof window !== 'undefined') {
        localStorage.setItem('auth_cache', JSON.stringify({
          data,
          timestamp: Date.now()
        }))
      }

      if (data.authenticated && data.user) {
        setSession({ user: data.user })
        setStatus('authenticated')
      } else {
        setSession(null)
        setStatus('unauthenticated')
      }
    } catch (error) {
      console.error('[useAuth] Error fetching session from server:', error)
      setSession(null)
      setStatus('unauthenticated')
    }
  }

  useEffect(() => {
    fetchSession()
  }, [])

  return {
    data: session,
    status,
    update: () => fetchSession(false), // Force refresh bez cache
  }
}

/**
 * Hook wymagający zalogowania - przekierowuje na /auth/signin jeśli nie zalogowany
 */
export function useRequireAuth(): UseAuthReturn {
  const router = useRouter()
  const auth = useAuth()

  useEffect(() => {
    if (auth.status === 'unauthenticated') {
      router.push('/auth/signin')
    }
  }, [auth.status, router])

  return auth
}

/**
 * Funkcja logowania (zastępuje signIn z NextAuth)
 */
export async function signIn(username: string, password: string): Promise<{
  success: boolean
  error?: string
}> {
  try {
    // Wyczyść stary cache przed logowaniem
    if (typeof window !== 'undefined') {
      localStorage.removeItem('auth_cache')
    }

    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    })

    const data = await res.json()

    if (res.ok && data.success) {
      return { success: true }
    } else {
      return { success: false, error: data.error || 'Login failed' }
    }
  } catch (error) {
    return { success: false, error: 'Network error' }
  }
}

/**
 * Funkcja wylogowania (zastępuje signOut z NextAuth)
 */
export async function signOut(): Promise<void> {
  try {
    // Wyczyść cache
    if (typeof window !== 'undefined') {
      localStorage.removeItem('auth_cache')
    }

    await fetch('/api/auth/logout', {
      method: 'POST',
    })
    // Przeładuj stronę żeby wyczyścić stan
    window.location.href = '/auth/signin'
  } catch (error) {
    console.error('[signOut] Error:', error)
    // Tak czy tak przekieruj na login
    window.location.href = '/auth/signin'
  }
}
